// src/components/Homepage.js
import React, { useState, useEffect } from "react";
import PublicHeader from "./public/PublicHeader";
import PublicFooter from "./public/PublicFooter";

// --- ASSETS CONFIGURATION ---
const HERO_IMAGES = ["/images/hero1.png", "/images/hero2.png"];
const INTRO_IMAGE = "/images/intro-student.jpg";

const UNI_LOGOS = [
  "Yale", "Stanford", "MIT", "Princeton",
  "Columbia", "UPenn", "Cornell", "Rice",
  "Emory", "CMU", "NYU", "SMU",
  "UOM", "UT-Austin", "UGA", "GT",
];

/** Match PublicHeader mobile breakpoint for consistent stacked layouts. */
const LAYOUT_BREAKPOINT_PX = 1024;

const styles = {
  page: {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    color: "#0f172a",
    background: "#ffffff",
    lineHeight: 1.6,
    margin: 0,
  },

  // ===== HERO (FULL-WIDTH BANNER) =====
  heroSection: {
    paddingTop: "24px",
    paddingBottom: "70px",
    background: "radial-gradient(circle at top, #f2f4fa 0, #ffffff 60%)",
  },
  heroInner: { width: "100%" },
  heroBannerShell: {
    width: "100%",
    margin: 0,
    borderRadius: 0,
    padding: 0,
    background: "none",
    boxShadow: "none",
    border: "none",
  },
  heroMedia: { width: "100%" },
  carouselContainer: {
    position: "relative",
    width: "100%",
    borderRadius: 0,
    overflow: "hidden",
    border: "none",
    aspectRatio: "16/6",
    background: "#f2f4fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  slideImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    transition: "transform 0.6s ease",
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,0.95)",
    border: "1px solid #e2e8f0",
    borderRadius: "999px",
    width: "40px",
    height: "40px",
    cursor: "pointer",
    fontSize: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 18px rgba(15,23,42,0.20)",
    zIndex: 2,
    color: "#0f172a",
  },
  arrowLeft: { left: "16px" },
  arrowRight: { right: "16px" },
  dotsContainer: {
    position: "absolute",
    bottom: "16px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "8px",
    zIndex: 2,
    padding: "6px 8px",
    borderRadius: "999px",
    background: "rgba(15,23,42,0.40)",
    backdropFilter: "blur(6px)",
  },
  dot: (active) => ({
    width: active ? "22px" : "8px",
    height: "8px",
    borderRadius: "999px",
    background: active ? "#ffffff" : "rgba(148,163,184,0.9)",
    cursor: "pointer",
    transition: "all 0.25s ease",
  }),

  // ===== SHARED CONTAINER =====
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "0 20px",
  },

  // ===== INTRO SECTION =====
  introSection: {
    marginTop: 0,
    padding: "70px 0",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  gridTwo: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)",
    columnGap: "44px",
    rowGap: "32px",
    alignItems: "center",
  },
  blurbBox: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    padding: "32px 32px 30px",
    boxShadow: "0 24px 50px rgba(15,23,42,0.08)",
  },
  blurbTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: "12px",
    lineHeight: 1.25,
    color: "#020617",
    overflowWrap: "break-word",
  },
  blurbSub: {
    fontSize: "13px",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#1d4ed8",
    fontWeight: 600,
    marginBottom: "10px",
  },
  blurbText: {
    fontSize: "14px",
    color: "#475569",
    lineHeight: 1.8,
  },
  blurbList: {
    marginTop: "16px",
    fontSize: "14px",
    color: "#0f172a",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  blurbListItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  blurbListIcon: {
    fontSize: "16px",
    lineHeight: 1,
    marginTop: "1px",
  },
  photoBox: {
    borderRadius: "22px",
    overflow: "hidden",
    border: "1px solid #dbe3ef",
    boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
    height: "100%",
    background: "#0f172a",
  },
  photoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  // ===== SERVICES SECTION =====
  servicesSection: {
    padding: "80px 20px 70px",
    background: "#ffffff",
  },
  serviceContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "center",
  },
  sectionHeader: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "26px",
    fontWeight: 700,
    marginBottom: "10px",
    color: "#020617",
  },
  sectionTag: {
    fontSize: "12px",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#1d4ed8",
    fontWeight: 600,
    marginBottom: "6px",
  },
  sectionText: {
    fontSize: "14px",
    color: "#64748b",
    lineHeight: 1.9,
  },
  featureGrid: {
    marginTop: "32px",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0,1fr))",
    gap: "18px",
  },
  featureCard: {
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    padding: "18px 18px 16px",
    textAlign: "left",
    background: "#f8fafc",
  },
  featureIconCircle: {
    width: "32px",
    height: "32px",
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    marginBottom: "10px",
    background: "rgba(37,99,235,0.08)",
  },
  featureTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "6px",
  },
  featureText: {
    fontSize: "13px",
    color: "#64748b",
  },

  // ===== SOCIAL PROOF (LOGOS) =====
  proofSection: {
    padding: "70px 20px 90px",
    background: "#ffffff",
  },
  proofHeaderWrap: {
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto 36px",
  },
  proofSubtitle: {
    fontSize: "12px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#1d4ed8",
    fontWeight: 600,
    marginBottom: "6px",
  },
  proofTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "24px",
    fontWeight: 700,
    color: "#020617",
    marginBottom: "6px",
  },
  proofText: {
    fontSize: "13px",
    color: "#64748b",
  },
  logoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
    gap: "26px",
    marginTop: "20px",
    alignItems: "center",
    justifyItems: "center",
  },
  logoItem: {
    height: "64px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  uniLogoImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
};

export default function Homepage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < LAYOUT_BREAKPOINT_PX
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < LAYOUT_BREAKPOINT_PX);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === HERO_IMAGES.length - 1 ? 0 : prev + 1));
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev === HERO_IMAGES.length - 1 ? 0 : prev + 1));
  const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? HERO_IMAGES.length - 1 : prev - 1));

  return (
    <div style={styles.page}>
      <PublicHeader />

      {/* ===== HERO: FULL-WIDTH CAROUSEL ONLY ===== */}
      <section
        style={{
          ...styles.heroSection,
          ...(isNarrow ? { paddingBottom: "40px" } : {}),
        }}
      >
        <div style={styles.heroInner}>
          <div style={styles.heroBannerShell}>
            <div style={styles.heroMedia}>
              <div
                style={{
                  ...styles.carouselContainer,
                  ...(isNarrow ? { aspectRatio: "16/10" } : {}),
                }}
              >
                <img
                  src={HERO_IMAGES[currentSlide]}
                  alt={`Center highlight ${currentSlide + 1}`}
                  style={styles.slideImg}
                />
                <button type="button" style={{ ...styles.arrow, ...styles.arrowLeft }} onClick={prevSlide}>
                  ‹
                </button>
                <button type="button" style={{ ...styles.arrow, ...styles.arrowRight }} onClick={nextSlide}>
                  ›
                </button>

                <div style={styles.dotsContainer}>
                  {HERO_IMAGES.map((_, idx) => (
                    <div
                      key={idx}
                      style={styles.dot(idx === currentSlide)}
                      onClick={() => setCurrentSlide(idx)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== INTRO SECTION ===== */}
      <section
        style={{
          ...styles.introSection,
          ...(isNarrow ? { padding: "40px 0" } : {}),
        }}
      >
        <div style={styles.container}>
          <div
            style={{
              ...styles.gridTwo,
              ...(isNarrow
                ? {
                    gridTemplateColumns: "1fr",
                    columnGap: 0,
                  }
                : {}),
            }}
          >
            <div
              style={{
                ...styles.blurbBox,
                ...(isNarrow ? { padding: "24px 20px 22px" } : {}),
              }}
            >
              <div style={styles.blurbSub}>College-Bound Mindset</div>
              <h3
                style={{
                  ...styles.blurbTitle,
                  ...(isNarrow ? { fontSize: "24px" } : {}),
                }}
              >
                Unlock your child’s future at top universities.
              </h3>
              <p style={styles.blurbText}>
                We specialize in guiding students toward highly selective schools such as Harvard, Stanford,
                Princeton, Yale, MIT, UC Berkeley, Columbia, Caltech, UPenn, Duke, University of Chicago, UVA,
                Emory, Rice, and many more.
                <br />
                <br />
                Our approach is rooted in the vision and expertise of our founder, who brings over two decades of
                experience in education, test prep, and college admissions counseling.
              </p>

              <div style={styles.blurbList}>
                <div style={styles.blurbListItem}>
                  <span style={styles.blurbListIcon}>🎯</span>
                  <span>Strategic long-term planning from middle school to college applications.</span>
                </div>
                <div style={styles.blurbListItem}>
                  <span style={styles.blurbListIcon}>📚</span>
                  <span>Subject tutoring, enrichment, and advanced coursework support.</span>
                </div>
                <div style={styles.blurbListItem}>
                  <span style={styles.blurbListIcon}>📝</span>
                  <span>Essay, resume, and activities guidance that tells your student’s story.</span>
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.photoBox,
                ...(isNarrow
                  ? {
                      height: "auto",
                      aspectRatio: "4/3",
                      alignSelf: "stretch",
                    }
                  : {}),
              }}
            >
              <img src={INTRO_IMAGE} alt="Student working with tutor" style={styles.photoImg} />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section style={styles.servicesSection}>
        <div style={styles.serviceContainer}>
          <div style={styles.sectionTag}>Programs & Services</div>
          <h4 style={styles.sectionHeader}>In-person & online test prep, tutoring, and college counseling.</h4>
          <p style={styles.sectionText}>
            At True Education, every student’s path is personal. We design fully customized programs for elementary,
            middle, and high school students—whether they need to catch up, stay on track, or get ahead.
          </p>

          <div
            style={{
              ...styles.featureGrid,
              ...(isNarrow ? { gridTemplateColumns: "1fr" } : {}),
            }}
          >
            <div style={styles.featureCard}>
              <div style={styles.featureIconCircle}>📖</div>
              <div style={styles.featureTitle}>Subject Tutoring</div>
              <div style={styles.featureText}>
                Math, reading, writing, science, AP courses, and more—with clear goals and ongoing progress updates.
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconCircle}>📈</div>
              <div style={styles.featureTitle}>Test Prep</div>
              <div style={styles.featureText}>
                SAT, ACT, PSAT, and other tests using proven strategies, targeted practice, and a clear improvement plan.
              </div>
            </div>

            <div style={styles.featureCard}>
              <div style={styles.featureIconCircle}>🎓</div>
              <div style={styles.featureTitle}>College Counseling</div>
              <div style={styles.featureText}>
                School list building, application timelines, essay coaching, and detailed guidance to stand out.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF SECTION ===== */}
      <section style={styles.proofSection}>
        <div style={styles.container}>
          <div style={styles.proofHeaderWrap}>
            <div style={styles.proofSubtitle}>College Outcomes</div>
            <h3 style={styles.proofTitle}>Our students have been accepted to:</h3>
            <p style={styles.proofText}>
              We are proud to support families whose children go on to thrive at respected universities.
            </p>
          </div>

          <div style={styles.logoGrid}>
            {UNI_LOGOS.map((name) => (
              <div key={name} style={styles.logoItem}>
                <img src={`/images/${name}.jpg`} alt={`${name} logo`} style={styles.uniLogoImg} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
